import { useState } from "react";
import { Check, ChevronDown, Loader2, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STARTER_PACK } from "@/lib/starterPack";
import { loadCustomTypologies, setCustomTypologies, layoutItem } from "@/lib/customTypologies";
import { ElevationThumb } from "@/components/ElevationThumb";

const sizeOf = (t) => `${t.layout.columns.reduce((a, b) => a + b, 0)} × ${t.layout.rows.reduce((a, b) => a + b, 0)}`;

const PackRow = ({ t, i, added, on, toggle }) => (
  <label className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${added ? "border-slate-800 opacity-50" : on ? "border-cyan-500/50 bg-cyan-500/5" : "border-slate-800 hover:bg-slate-800/40"} cursor-pointer`} data-testid={`starter-row-${i}`}>
    <input type="checkbox" checked={added || on} disabled={added} onChange={toggle} data-testid={`starter-check-${i}`} className="accent-cyan-500 w-4 h-4" />
    <div className="w-14 h-12 rounded bg-[#090D16] border border-slate-800 p-1 shrink-0"><ElevationThumb item={layoutItem(t)} size={52} /></div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-100 truncate">{t.label}<span className="ml-2 font-mono text-[10px] text-cyan-300">{t.code}</span></p>
      <p className="text-[10px] text-slate-500 truncate">{t.type} · {sizeOf(t)} mm · {t.system.replace("YKK AP IWIN-S ", "IWIN-S ")}</p>
    </div>
    {added && <span className="text-[10px] font-mono uppercase text-emerald-300 flex items-center gap-1" data-testid={`starter-added-${i}`}><Check size={11} />Added</span>}
  </label>
);

export const StarterPackPanel = ({ existing }) => {
  const names = new Set(existing.map((t) => t.label.toLowerCase()));
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(() => new Set(STARTER_PACK.map((_, i) => i)));
  const [busy, setBusy] = useState(false);
  const isAdded = (t) => names.has(t.label.toLowerCase());
  const chosen = STARTER_PACK.filter((t, i) => sel.has(i) && !isAdded(t));
  const toggle = (i) => setSel((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const add = async () => {
    setBusy(true);
    let ok = 0, skipped = 0;
    for (const t of chosen) {
      try { await api.post("/typologies", t); ok++; } catch (e) { if (e?.response?.status === 409) skipped++; else toast.error(`Could not add "${t.label}"`); }
    }
    setCustomTypologies(await loadCustomTypologies(true));
    setBusy(false);
    if (ok) toast.success(`Added ${ok} starter typolog${ok === 1 ? "y" : "ies"}${skipped ? ` · ${skipped} already existed` : ""}`);
    else if (skipped) toast.info("Those typologies already exist");
  };
  const left = STARTER_PACK.filter((t) => !isAdded(t)).length;
  return (
    <div className="rounded-lg border border-slate-800 bg-[#0d1220]" data-testid="starter-pack">
      <button data-testid="starter-pack-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-800/40 transition-colors rounded-lg">
        <PackagePlus size={15} className="text-cyan-300" />
        <span className="text-sm text-slate-100 flex-1">Starter pack · {STARTER_PACK.length} common Indian layouts</span>
        <span className="font-mono text-[10px] text-slate-500" data-testid="starter-pack-remaining">{left ? `${left} not added` : "all added"}</span>
        <ChevronDown size={15} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-center gap-3 text-[11px]">
            <button data-testid="starter-select-all" onClick={() => setSel(new Set(STARTER_PACK.map((_, i) => i)))} className="text-cyan-300 hover:text-cyan-200">Select all</button>
            <button data-testid="starter-select-none" onClick={() => setSel(new Set())} className="text-slate-400 hover:text-slate-200">None</button>
            <span className="ml-auto text-slate-500">Typical Indian sizes · Bhoruka IWIN-S series pre-set · edit or delete any time</span>
          </div>
          <div className="grid gap-1.5 max-h-64 overflow-y-auto pr-1">
            {STARTER_PACK.map((t, i) => <PackRow key={t.code} t={t} i={i} added={isAdded(t)} on={sel.has(i)} toggle={() => toggle(i)} />)}
          </div>
          <button data-testid="starter-add-btn" onClick={add} disabled={busy || !chosen.length}
            className="h-9 w-full rounded-md bg-cyan-500 text-slate-950 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-cyan-400 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <PackagePlus size={15} />}Add {chosen.length} selected typolog{chosen.length === 1 ? "y" : "ies"}
          </button>
        </div>
      )}
    </div>
  );
};
